import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { leerVolverA } from '../utils/volverA';

/**
 * Breadcrumb — ADR-2026-08-03-breadcrumbs-pantallas-de-modo y
 * ADR-2026-08-03-breadcrumbs-asistente-plantillas-estudio.
 *
 * Niveles anteriores en gris y clickeables, barra separadora más clara todavía,
 * nivel actual en negro sólido y algo más pesado. Es el patrón de la referencia que
 * trajo el usuario (Klarna).
 *
 * Nació dentro de `PresetDetailSheet` y se extrajo al sumarse el asistente,
 * `/plantillas` y el estudio de video: son cuatro lugares con el mismo dibujo, y la
 * pieza no depende de nada del sheet.
 *
 * **Ojo con los colores.** Los grises van con el `black` de Tailwind y no con el
 * token de marca: `brand.black` está declarado como `var(--brand-black)` sin
 * `<alpha-value>`, así que `text-brand-black/45` **no genera ninguna regla** y el
 * texto se queda con el color heredado — los tres niveles salían del mismo negro,
 * sin jerarquía. El nivel actual sí usa el token, pero sin modificador.
 *
 * **Volver a donde vine** (ADR-2026-09-16-volver-a-donde-vine). Casi todas las
 * pantallas ponen "Inicio" como primer nivel y lo mandan a la home; pero si a esta
 * pantalla se llegó con `volverA` en el state (desde el Agente, por ejemplo), la única
 * salida visible en escritorio tiene que devolver AHÍ. Se resuelve acá, en la pieza que
 * todas comparten, en vez de tocar catorce pantallas: el primer nivel pasa a decir
 * "Volver" y a ir al origen. Sólo el primero, y sólo si era clickeable.
 */

/** Un nivel del breadcrumb. Sin `onClick` es texto, no link. */
export interface Miga {
  label: string;
  /** A dónde vuelve este nivel. El último lo ignora: es donde ya estás parado. */
  onClick?: () => void;
}

export function Migas({
  items,
  className = '',
}: {
  items: Miga[];
  /** Extra del contenedor — lo usa el sheet para la pastilla blanca del layout
   *  full-screen, donde el header es transparente y flota sobre las cards. */
  className?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const volverA = leerVolverA(location.state);
  if (items.length === 0) return null;
  const migas =
    volverA && items.length > 1 && items[0].onClick
      ? [{ label: 'Volver', onClick: () => navigate(volverA.path, { state: volverA.state }) }, ...items.slice(1)]
      : items;
  return (
    <nav
      aria-label="Ubicación"
      className={`inline-flex items-center gap-2 text-[14px] ${className}`}
    >
      {migas.map((miga, i) => {
        const ultimo = i === migas.length - 1;
        return (
          <React.Fragment key={`${miga.label}-${i}`}>
            {i > 0 && (
              // La barra va aparte y más clara que los dos textos que separa: es
              // puntuación, no un nivel más.
              <span aria-hidden="true" className="text-black/25">
                /
              </span>
            )}
            {/* El último nivel es dónde estás parado: ni link ni gris. `aria-current`
                es lo que se lo dice a un lector de pantalla, que no ve el color. */}
            {ultimo || !miga.onClick ? (
              <span
                aria-current={ultimo ? 'page' : undefined}
                className={ultimo ? 'font-medium text-brand-black' : 'text-black/45'}
              >
                {miga.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={miga.onClick}
                className="text-black/45 transition-colors hover:text-black/80"
              >
                {miga.label}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default Migas;
